import { derived, type Readable } from "svelte/store";
import Delaunator from "delaunator";
import Graph from "dijkstra";
import { appendPair } from "./svg";
import { isWithin, distance, radians, boundingBox, type Rect } from "./geometry";
import { flatMap } from "lodash-es";

export interface Waypoint {
  id: string;
  placeId?: string;
  x: number;
  y: number;
  gravity: number;
  radius: number;
  shape: string;
  rotation: number;
  hazard: boolean;
}

export function getConnectionX(point: Waypoint) {
  if (point.shape === "point") {
    return point.x + point.gravity * Math.cos(radians(point.rotation));
  }

  return point.x;
}

export function getConnectionY(point: Waypoint) {
  if (point.shape === "point") {
    return point.y + point.gravity * Math.sin(radians(point.rotation));
  }

  return point.y;
}

function hitRadius(point: Waypoint) {
  return point.shape == "point" ? 4 : point.gravity;
}

function touches(pointA: Waypoint, pointB: Waypoint) {
  return isWithin(
    getConnectionX(pointA),
    getConnectionY(pointA),
    pointB.x,
    pointB.y,
    hitRadius(pointA) + hitRadius(pointB)
  );
}

function distanceTo(pointA: Waypoint, pointB: Waypoint) {
  return Math.max(0.01, distance(pointA.x, pointA.y, pointB.x, pointB.y));
}

export function deriveCollisionNetwork(points$: Readable<Array<Waypoint>>) {
  return derived(points$, collisionNetwork);
}

export function collisionNetwork(points: Array<Waypoint>) {
  return reduceCollisionNetwork(
    points,
    function (graph, pointA, pointB) {
      return graph.connect(pointA, pointB, distanceTo(pointA, pointB));
    },
    new Graph<Waypoint>()
  );
}

type CalculateRouteOptions = {
  allowHazards?: boolean;
};

export function calculateRoute(
  waypoints: Array<Waypoint>,
  user: Waypoint,
  active: Array<Waypoint>,
  options: CalculateRouteOptions
) {
  const points = waypoints.concat(user).filter((i) => {
    return options.allowHazards || !i.hazard;
  });

  const routes = collisionNetwork(points);

  return active
    .map((waypoint: Waypoint) => {
      const { path, cost } = routes.path(user, waypoint, {
        reverse: true,
        cost: true
      });

      if (path.length === 0) {
        return null;
      }

      return { waypoint, path, cost };
    })
    .filter((i) => i);
}

export function currentEventLines(
  waypoints: Array<Waypoint>,
  user: Waypoint,
  active: Array<Waypoint>
) {
  if (user == null) {
    return { withHazards: [], withoutHazards: [] };
  }

  const withHazards = calculateRoute(waypoints, user, active, {
    allowHazards: true
  });

  const withoutHazards = calculateRoute(waypoints, user, active, {
    allowHazards: false
  });

  return { withHazards, withoutHazards };
}

export function buildWaypointNetworkPath(waypoints: Array<Waypoint>) {
  return reduceCollisionNetwork(waypoints, appendPair, "");
}

type CollisionReducer<Type> = (initial: Type, pointA: Waypoint, pointB: Waypoint) => Type;

export function reduceCollisionNetwork<Type>(
  points: Array<Waypoint>,
  reducer: CollisionReducer<Type>,
  initial: Type
) {
  const { triangles } = Delaunator.from<Waypoint>(
    points,
    (p) => p.x,
    (p) => p.y
  );

  function connectClose(a: number, b: number) {
    const pointA = points[a];
    const pointB = points[b];

    if (touches(pointA, pointB)) {
      initial = reducer(initial, pointA, pointB);
    }
  }

  for (let i = 0; i < triangles.length; i += 3) {
    const p0 = triangles[i];
    const p1 = triangles[i + 1];
    const p2 = triangles[i + 2];

    connectClose(p0, p1);
    connectClose(p1, p0);

    connectClose(p1, p2);
    connectClose(p2, p1);

    connectClose(p2, p0);
    connectClose(p0, p2);
  }

  return initial;
}

export type ConnectionPath = {
  waypoint: Waypoint;
  path: Array<Rect>;
  cost: number;
};

export function pathBoundingBox(paths: Array<ConnectionPath>) {
  return boundingBox(flatMap(paths, (i) => i.path));
}
